import { LinkBreak2Icon, ReaderIcon } from '@radix-ui/react-icons';
import { VariantProps } from 'class-variance-authority';
import Lottie from 'lottie-react';
import { HTMLAttributes, useEffect, useState, type JSX } from 'react';

import { LoaderCircleIcon } from '../../svg/loader-circle-icon';
import { cn } from '../../utils/cn';
import { thumbnailVariants } from '../thumbnail/thumbnail-variants';
import { validateLottieFile } from './is-lottie.util';

interface LottieFileProps
  extends
    Pick<HTMLAttributes<HTMLDivElement>, 'className'>,
    VariantProps<typeof thumbnailVariants> {
  animationData: unknown;
  fallback?: JSX.Element;
}

export function LottieFile({ animationData, className, fallback }: LottieFileProps) {
  const [resolvedData, setResolvedData] = useState<any | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [failedAtRuntime, setFailedAtRuntime] = useState(false);
  const [isInvalidLottie, setIsInvalidLottie] = useState(false);

  useEffect(() => {
    let cancelled = false;

    async function validate() {
      setIsLoading(true);
      setFailedAtRuntime(false);
      setIsInvalidLottie(false);

      const { isLottie, animationData: parsed } = await validateLottieFile(animationData);
      if (!cancelled) {
        setResolvedData(isLottie ? parsed : null);
        setIsInvalidLottie(!isLottie);
        setIsLoading(false);
      }
    }

    validate();

    return () => {
      cancelled = true;
    };
  }, [animationData]);

  if (isLoading) {
    return (
      <div className={cn('flex items-center justify-center text-muted-foreground', className)}>
        <LoaderCircleIcon className="animate-spin" />
      </div>
    );
  }

  if (isInvalidLottie) {
    // eslint-disable-next-line react/jsx-no-useless-fragment
    if (fallback) return <>{fallback}</>;

    return (
      <div className={cn('flex items-center justify-center text-muted-foreground', className)}>
        <ReaderIcon />
      </div>
    );
  }

  if (failedAtRuntime || !resolvedData) {
    return (
      <div className={cn('flex items-center justify-center text-muted-foreground', className)}>
        <LinkBreak2Icon />
      </div>
    );
  }

  return (
    <div className={cn('flex items-center justify-center overflow-hidden', className)}>
      <Lottie animationData={resolvedData} loop onError={() => setFailedAtRuntime(true)} />
    </div>
  );
}
